
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Layers, FileOutput, ChevronLeft, ChevronRight, Scan, MoveHorizontal, Quote, ArrowRight } from "lucide-react";
import SciFiButton from "../Props/SciFiButton";
import GetStartedButton from "../Props/GetStartedButton";

interface VideoBackgroundProps {
  source: string;
  className?: string;
}

const VideoScreen: React.FC<VideoBackgroundProps> = ({ source, className }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [loading, setLoading] = useState(true);

  // Attempt to play the video reliably
  const attemptPlay = () => {
    if (videoRef.current) {
      videoRef.current.muted = true; // Strict mute enforcement
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch((e) => {
          console.log("Autoplay prevented:", e);
          // Optional: Add a user interaction listener here if needed, 
          // but for background videos, muted should just work.
        });
      }
    }
  };

  useEffect(() => {
    attemptPlay();
  }, [source]);

  return (
    <div className={`absolute overflow-hidden bg-black ${className}`}>
      <video
        key={source}
        ref={videoRef}
        src={source}
        muted={true}
        loop
        autoPlay
        playsInline
        preload="auto"
        className="w-full h-full object-cover opacity-100"
        onLoadedData={() => {
          console.log("Video data loaded");
          setLoading(false);
          attemptPlay();
        }}
        onCanPlay={() => {
          console.log("Video can play");
          attemptPlay();
        }}
        onError={(e) => console.error("Video error:", e)}
      />
    </div>
  );
};


const GlitchChar = ({
  char,
  index,
  isVisible,
  onComplete,
  isEditorialFont
}: {
  char: string;
  index: number;
  isVisible: boolean;
  onComplete?: () => void;
  isEditorialFont?: boolean;
}) => {
  const [displayChar, setDisplayChar] = useState("");
  const [isDone, setIsDone] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (!isVisible || hasStarted) return;

    // Timer to start animation for this character
    const startDelay = index * 120; // Faster sequential staggered start

    const timer = setTimeout(() => {
      // START ANIMATION HERE (inside timeout to avoid cleanup race condition)
      setHasStarted(true);

      let frames = 0;
      const maxFrames = 8 + Math.floor(Math.random() * 6);

      const interval = setInterval(() => {
        if (frames < maxFrames) {
          const glitches = ["*", "ø", "!", "X", "2", "§", "Δ", "0", "1", "¿"];
          if (char.toLowerCase() === 'o') {
            setDisplayChar(frames % 2 === 0 ? "ø" : glitches[Math.floor(Math.random() * glitches.length)]);
          } else if (char.toLowerCase() === 't' && index >= 7 && index < 10) {
            setDisplayChar(frames % 2 === 0 ? "2" : "t");
          } else {
            setDisplayChar(glitches[Math.floor(Math.random() * glitches.length)]);
          }
          frames++;
        } else {
          setDisplayChar(char);
          setIsDone(true);
          onComplete?.();
          clearInterval(interval);
        }
      }, 70); // Frame duration

      // Cleanup local interval if component unmounts (though logic is mostly self-contained)
      return () => clearInterval(interval);
    }, startDelay);

    return () => clearTimeout(timer);
  }, [isVisible, char, index, onComplete, hasStarted]);

  // Special Case: Word "PROMPT" - the 'o' is at index 2
  if (index === 2 && isDone) {
    return (
      <span className="inline-block w-[1ch]">
        ø
      </span>
    );
  }

  return (
    <span className={`inline-block transition-opacity duration-300 ${hasStarted ? 'opacity-100' : 'opacity-0'} ${isEditorialFont ? "font-serif italic font-normal w-auto" : "w-[1ch]"}`}>
      {displayChar || (isDone ? char : char)}
    </span>
  );
};

const TypewriterHeading = ({ theme }: { theme: 'orange' | 'blue' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const fullText = "Prompt-to-Event";

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.4 }
    );
    if (containerRef.current) observer.observe(containerRef.current);

    const check = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const height = window.innerHeight;
        const visibleHeight = Math.min(rect.bottom, height) - Math.max(rect.top, 0);
        if (visibleHeight / rect.height >= 0.4) {
          setIsVisible(true);
        }
      }
    };
    check();
    window.addEventListener('scroll', check);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', check);
    };
  }, []);

  const handleCharComplete = useCallback(() => {
    setCompletedCount(prev => prev + 1);
  }, []);

  const allDone = completedCount >= fullText.length;

  return (
    <div ref={containerRef} className="flex flex-col gap-6">
      <h2 className="font-mono font-bold text-[56px] md:text-[84px] leading-[1] tracking-tighter flex flex-wrap items-center">
        {/* Full sequence in one flow, but wrapping 'Event' to prevent break */}
        {fullText.slice(0, 10).split("").map((char, i) => (
          <GlitchChar
            key={i}
            char={char}
            index={i}
            isVisible={isVisible}
            onComplete={handleCharComplete}
          />
        ))}

        {/* Event Group - NoWrap, Color, Editorial Font */}
        <span className={`flex items-center ml-2 whitespace-nowrap ${theme === 'orange' ? 'text-[#FF5252]' : 'text-[#0055FF]'}`}>
          {fullText.slice(10).split("").map((char, i) => (
            <GlitchChar
              key={i + 10}
              char={char}
              index={i + 10}
              isVisible={isVisible}
              onComplete={handleCharComplete}
              isEditorialFont
            />
          ))}
        </span>

        {isVisible && !allDone && (
          <span className="w-[2px] h-[0.8em] bg-current animate-pulse ml-1 self-center" />
        )}
      </h2>
      <p className={`font-sans text-[20px] md:text-[24px] opacity-60 leading-tight transition-all duration-1000 delay-[2s] ${allDone ? 'opacity-60 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        Create&nbsp;layouts&nbsp;of your&nbsp;event&nbsp;in&nbsp;minutes.
      </p>
    </div>
  );
};

const ContentLayout = ({ theme }: { theme: 'orange' | 'blue' }) => {
  const color = theme === 'orange' ? 'text-[#FF5252]' : 'text-[#0055FF]';
  const borderColor = theme === 'orange' ? 'border-[#FF5252]/30' : 'border-[#0055FF]/30';
  const gridColor = theme === 'orange' ? 'bg-[#FF5252]' : 'bg-[#0055FF]';
  const buttonPrimary = theme === 'orange' ? '#FF5252' : '#0055FF';

  return (
    <div className={`w-full h-full relative ${color}`}>
      {/* SWISS GRID LINES */}
      <div className="absolute inset-0 w-full h-full  mx-auto pointer-events-none">

        {/* Vertical Grid Sets (3 Sets x 2 Lines = 6 Lines total) */}
        <div className="absolute inset-0 h-full w-full">
          {/* Left Set - Shifted slightly right */}
          <div className="absolute top-0 bottom-0 left-[23%] flex gap-[5px] h-full">
            <div className={`w-px h-full ${gridColor} opacity-30`} />
            <div className={`w-px h-full ${gridColor} opacity-30`} />
          </div>

          {/* Right Set 1 - Right of center */}
          <div className="absolute top-0 bottom-0 left-[64%] flex gap-[5px] h-full">
            <div className={`w-px h-full ${gridColor} opacity-30`} />
            <div className={`w-px h-full ${gridColor} opacity-30`} />
          </div>

          {/* Right Set 2 - Clustered with Set 1 */}
          <div className="absolute top-0 bottom-0 left-[74.9%] flex gap-[5px] h-full">
            <div className={`w-px h-full ${gridColor} opacity-30`} />
            <div className={`w-px h-full ${gridColor} opacity-30`} />
          </div>
        </div>

        {/* Horizontal Grid Sets (2 Sets x 2 Lines = 4 Lines total) */}
        <div className="absolute inset-0 flex flex-col justify-evenly h-full w-full">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-[5px] w-full">
              <div className={`h-px w-full ${gridColor} opacity-30`} />
              <div className={`h-px w-full ${gridColor} opacity-30`} />
            </div>
          ))}
        </div>
      </div>

      {/* CONTENT LAYER */}
      <div className="absolute inset-0 w-full h-full max-w-[1600px] mx-auto px-6 lg:px-12 py-24 flex flex-col justify-between">

        {/* TOP RIGHT CTA BUTTON */}
        <div className="absolute top-56 left-6 sm:top-64 lg:right-12 lg:left-auto lg:top-32 z-50">
          <GetStartedButton
            className="w-[185px] !h-[52px] bg-white/80 backdrop-blur-md"
            primaryColor={buttonPrimary}
            borderColor={borderColor}
            textColor={`text-${theme === 'orange' ? '[#FF5252]' : '[#0055FF]'}`}
          />
        </div>

        {/* TOP: QUOTE / HEADLINE */}
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 relative">
            <TypewriterHeading theme={theme} />
          </div>
        </div>

        {/* MIDDLE: THE CENTERPIECE IMAGE */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[45%] w-[80vw] md:w-[60vw] lg:w-[50vw] max-w-[800px] aspect-[4/3] z-10">
          {theme === 'orange' ? (
            // ORANGE SIDE: CAD
            <div className="w-full h-full relative">
              <img
                src="/assets/Booth blueprint.png"
                className="w-full contrast-125 h-full object-cover"
                alt="Render"
              />
              {/* Schematic Annotations */}
              <div className="absolute bottom-0 right-4 text-[#FF5252] text-[10px] font-mono text-right">
                SCALE: 1:50<br />
                EXHIBITOR: A-101
              </div>
            </div>
          ) : (
            // BLUE SIDE: RENDER
            <div className="w-full h-full relative">
              {/* 1. VIDEO (Background Layer - z-0) */}
              <VideoScreen
                source="/assets/Booth_video.mp4"
                className="top-[39.5%] left-[7%] w-[85%] h-[20%] z-0"
              />

              {/* 2. RENDER IMAGE (Foreground Layer - z-10) */}
              <img
                src="/assets/Booth blue.png"
                className="absolute contrast-125 inset-0 w-full h-full object-cover z-10"
                alt="Render"
                style={{ transform: 'scale(1.01)' }}
              />
            </div>
          )}
        </div>

        {/* BOTTOM: FEATURES */}
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-6 lg:col-span-3">
            <h3 className="font-mono text-[16px] mb-2 opacity-70">
              "Isometric, open-sided exhibition booth with corporate branding."
            </h3>
          </div>

          <div className="col-span-6 pl-1 lg:col-span-3 lg:col-start-10">
            <div className="flex items-start flex-col justify-between">
              <h3 className="font-mono text-[18px] mb-2 font-sembold">Import your designs</h3>
              <span className="font-mono text-gray-600 text-[12px] opacity-60 tracking-widest">
                Export DWG/PDF/GLB from a single source of truth.
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

const SolutionSection = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const blueLayerRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const state = useRef({
    isDragging: false,
    currentPos: 50,
  });
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    const animate = () => {
      const s = state.current;
      if (!s.isDragging) {
        const time = Date.now() / 2500;
        // Reduced amplitude to 12% (Range 38% - 62% = approx 60% coverage max deviation)
        const sway = 50 + Math.sin(time) * 12;
        s.currentPos += (sway - s.currentPos) * 0.05;
      }

      if (blueLayerRef.current && sliderRef.current) {
        const pos = Math.round(s.currentPos * 100) / 100;
        blueLayerRef.current.style.clipPath = `inset(0 ${100 - pos}% 0 0)`;
        sliderRef.current.style.left = `${pos}%`;
      }
      animationRef.current = requestAnimationFrame(animate);
    };
    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current!);
  }, []);

  const handleMouseDown = () => {
    state.current.isDragging = true;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!state.current.isDragging || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    state.current.currentPos = (x / rect.width) * 100;
  };

  const handleMouseUp = () => {
    state.current.isDragging = false;
  };

  useEffect(() => {
    window.addEventListener("mouseup", handleMouseUp);
    return () => window.removeEventListener("mouseup", handleMouseUp);
  }, []);

  return (
    <section
      ref={containerRef}
      className="relative w-full h-screen bg-white overflow-hidden select-none"
      // Added touch handlers for mobile support if needed later, but sticking to mouse for now
      onMouseMove={handleMouseMove}
      onMouseDown={handleMouseDown}
    >
      {/* 1. LAYER ONE: ORANGE (Bottom) */}
      <div className="absolute inset-0 z-0">
        <ContentLayout theme="orange" />
      </div>

      {/* 2. LAYER TWO: BLUE (Top, Clipped) */}
      <div
        ref={blueLayerRef}
        className="absolute inset-0 z-10 overflow-hidden bg-white"
        style={{
          clipPath: `inset(0 50% 0 0)`,
          willChange: 'clip-path'
        }}
      >
        <ContentLayout theme="blue" />
      </div>

      {/* 3. SLIDER HANDLE */}
      <div
        ref={sliderRef}
        className="absolute top-0 bottom-0 z-40 w-[2px] bg-[#FF5252] pointer-events-none"
        style={{ left: `50%` }}
      >
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto cursor-grab active:cursor-grabbing"
          onMouseDown={handleMouseDown}
        >
          <svg width="40" height="60" viewBox="0 0 40 60" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M18 0H22V20C22 29 23 29 40 29V31C23 31 22 31 22 40V60H18V40C18 31 17 31 0 31V29C17 29 18 29 18 20V0Z"
              fill="white"
              stroke="#FF5252"
              strokeWidth="2"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
